import React, {useEffect, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';

export default function PageEditor() {
  const {id: courseId, pageId} = useParams();
  const [page, setPage] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const fileRef = useRef();

  const load = async () => {
    const res = await api(`/lms/courses/${courseId}/pages`);
    if (res.ok) {
      const pages = await res.json();
      const p = pages.find(pg => pg.id === pageId);
      if (p) {
        setPage(p); setTitle(p.title); setContent(p.content || '');
        if (p.pageType === 'QUIZ' && p.content) {
          try { setQuestions(JSON.parse(p.content)); } catch(e) { setQuestions([]); }
        }
      }
    }
  };

  useEffect(() => { load(); }, [courseId, pageId]);

  const handleSave = async () => {
    setSaving(true);
    const body = { title, pageType: page.pageType, content: page.pageType === 'QUIZ' ? JSON.stringify(questions) : content };
    const res = await api(`/lms/courses/${courseId}/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    const formData = new FormData();
    formData.append('file', file);
    await fetch(`${API_BASE}/lms/courses/${courseId}/pages/${pageId}/video-upload`, { method: 'POST', headers, body: formData });
    fileRef.current.value = '';
    setUploading(false);
    load();
  };

  if (!page) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="mb-6">
        <Link to={`/lms/courses/${courseId}`} className="text-violet-600 hover:text-violet-800 text-sm">← Back to Course</Link>
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-green-700 text-sm">Changes saved.</div>
      )}

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${page.pageType === 'VIDEO' ? 'bg-red-50 text-red-600' : page.pageType === 'QUIZ' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
            {page.pageType}
          </span>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none" />
          </div>

          {page.pageType === 'CONTENT' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (HTML)</label>
              <textarea value={content} onChange={e => setContent(e.target.value)} rows={12}
                placeholder="Enter HTML content for this slide..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none font-mono text-sm" />
              {content && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                  <div className="border rounded-lg p-6 prose max-w-none" dangerouslySetInnerHTML={{__html: content}} />
                </div>
              )}
            </div>
          )}

          {page.pageType === 'VIDEO' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video</label>
              {page.videoS3Key ? (
                <div className="text-sm text-green-600 mb-2">✓ Video uploaded</div>
              ) : (
                <div className="text-sm text-amber-600 mb-2">No video uploaded yet</div>
              )}
              <label className={`inline-flex px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-sm ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                {uploading ? 'Uploading...' : 'Upload Video'}
                <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} disabled={uploading} />
              </label>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (shown below video)</label>
                <textarea value={content} onChange={e => setContent(e.target.value)} rows={4}
                  placeholder="Optional description or notes for this video..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none" />
              </div>
            </div>
          )}

          {page.pageType === 'QUIZ' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Questions</label>
              {questions.map((q, qi) => (
                <div key={qi} className="border rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-500">Question {qi + 1}</span>
                    <button type="button" onClick={() => setQuestions(questions.filter((_, i) => i !== qi))} className="text-red-400 hover:text-red-600 text-sm">Remove</button>
                  </div>
                  <input type="text" value={q.question} onChange={e => { const u = [...questions]; u[qi] = {...u[qi], question: e.target.value}; setQuestions(u); }}
                    placeholder="Question text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none mb-2" />
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2 mb-1">
                      <input type="radio" name={`correct-${qi}`} checked={q.correctIndex === oi}
                        onChange={() => { const u = [...questions]; u[qi] = {...u[qi], correctIndex: oi}; setQuestions(u); }} />
                      <input type="text" value={opt} onChange={e => { const u = [...questions]; const opts = [...u[qi].options]; opts[oi] = e.target.value; u[qi] = {...u[qi], options: opts}; setQuestions(u); }}
                        placeholder={`Option ${oi + 1}`} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 outline-none text-sm" />
                      {q.options.length > 2 && (
                        <button type="button" onClick={() => { const u = [...questions]; const opts = q.options.filter((_, i) => i !== oi); const ci = q.correctIndex >= oi && q.correctIndex > 0 ? q.correctIndex - 1 : q.correctIndex; u[qi] = {...u[qi], options: opts, correctIndex: Math.min(ci, opts.length - 1)}; setQuestions(u); }}
                          className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => { const u = [...questions]; u[qi] = {...u[qi], options: [...u[qi].options, '']}; setQuestions(u); }}
                    className="text-violet-600 hover:text-violet-800 text-sm mt-1">+ Add Option</button>
                </div>
              ))}
              <button type="button" onClick={() => setQuestions([...questions, {question: '', options: ['', ''], correctIndex: 0}])}
                className="px-3 py-1.5 border border-violet-300 text-violet-600 rounded-lg hover:bg-violet-50 text-sm">
                + Add Question
              </button>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
